/**
 * StepContent.tsx  (Step 3.5 — Brand Details)
 *
 * Shown between template selection and carousel generation.
 * Its only job is to collect any brand fields that are required by the selected
 * template but are not yet in the user's profile (e.g. LinkedIn handle, tagline).
 *
 * Auto-skip: if all required brand fields are already filled from the user's
 * profile, this step is transparent — it shows a "preparing" spinner and
 * immediately calls onGenerate() after mount (by which time all state from the
 * structure call has been committed to React).
 */

import React from "react";
import type { BrandField } from "./types";
import { primaryBtn, ghostBtn, stepHeading, stepSub, ACCENT, ACCENT_40 } from "./sharedStyles";

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  brandFields:    BrandField[];
  brandValues:    Record<string, string>;
  brandOriginals: Record<string, string>;
  isSavingBrand:  boolean;
  brandSaveError: string | null;
  brandSaved:     boolean;
  setBrandValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setBrandSaved:  (v: boolean) => void;
  onSaveBrand:    () => void;
  onGenerate:     () => void;
  onBack:         () => void;
}

// ── Shared field input style ──────────────────────────────────────────────────

const fieldInput: React.CSSProperties = {
  width:        "100%",
  boxSizing:    "border-box",
  border:       "1.5px solid #E4E4E0",
  borderRadius: "10px",
  padding:      "9px 14px",
  outline:      "none",
  fontFamily:   "'Bricolage Grotesque', sans-serif",
  fontSize:     "14px",
  background:   "#fff",
  color:        "#0A0A0A",
  transition:   "border-color 0.15s",
};

// ── Component ─────────────────────────────────────────────────────────────────

const StepContent: React.FC<Props> = ({
  brandFields, brandValues, brandOriginals,
  isSavingBrand, brandSaveError, brandSaved,
  setBrandValues, setBrandSaved, onSaveBrand, onGenerate, onBack,
}) => {
  // Auto-skip is handled by the parent (index.tsx useEffect watching step + brandFields).
  // This component only renders when there ARE missing brand fields to fill.

  const hasChanges         = brandFields.some(f => (brandValues[f.key] ?? "") !== (brandOriginals[f.key] ?? ""));
  const hasMissingUnfilled = brandFields.some(f => f.isMissing && !brandValues[f.key]?.trim());

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>
        <p style={stepHeading}>Brand details</p>
        <p style={stepSub}>
          This template uses your brand information. Fill in any missing fields to personalise your carousel.
        </p>
      </div>

      {/* Brand fields card */}
      <BrandFieldsPanel
        fields={brandFields}
        values={brandValues}
        originals={brandOriginals}
        hasChanges={hasChanges}
        hasMissingUnfilled={hasMissingUnfilled}
        isSaving={isSavingBrand}
        saveError={brandSaveError}
        saved={brandSaved}
        onChange={(key, val) => {
          setBrandValues(prev => ({ ...prev, [key]: val }));
          setBrandSaved(false);
        }}
        onSave={onSaveBrand}
        onChooseOtherTemplate={onBack}
      />

      {/* Navigation */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button style={ghostBtn} onClick={onBack}>← Change template</button>
        <button style={primaryBtn} onClick={onGenerate}>
          Generate carousel →
        </button>
      </div>
    </div>
  );
};

// ── BrandFieldsPanel ──────────────────────────────────────────────────────────

/**
 * Card showing required brand fields (handle, tagline, etc.).
 * Missing fields are highlighted in amber to draw attention.
 */
const BrandFieldsPanel: React.FC<{
  fields:                BrandField[];
  values:                Record<string, string>;
  originals:             Record<string, string>;
  hasChanges:            boolean;
  hasMissingUnfilled:    boolean;
  isSaving:              boolean;
  saveError:             string | null;
  saved:                 boolean;
  onChange:              (key: string, val: string) => void;
  onSave:                () => void;
  onChooseOtherTemplate: () => void;
}> = ({
  fields, values, originals, hasChanges, hasMissingUnfilled,
  isSaving, saveError, saved,
  onChange, onSave, onChooseOtherTemplate,
}) => (
  <div style={{
    border:       `1.5px solid ${hasMissingUnfilled ? "#f0c040" : "#E4E4E0"}`,
    borderRadius: "14px",
    padding:      "18px 20px",
    background:   hasMissingUnfilled ? "#fffbf0" : "#fafaf9",
  }}>
    <p style={{
      fontSize: "12px", fontWeight: 700, color: "#444",
      letterSpacing: "0.05em", textTransform: "uppercase",
      marginBottom: "14px",
    }}>
      Brand details for this template
    </p>

    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {fields.map(field => {
        const val     = values[field.key] ?? "";
        const changed = val !== (originals[field.key] ?? "");
        const missing = field.isMissing && !val.trim();
        return (
          <div key={field.key}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "5px" }}>
              <label style={{
                fontSize: "11px", fontWeight: 700,
                color: missing ? "#9a7800" : "#666",
                letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                {field.label}
              </label>
              {missing  && <span style={{ fontSize: "10px", color: "#d4a800", fontWeight: 600 }}>required</span>}
              {!missing && changed && <span style={{ fontSize: "10px", color: ACCENT, fontWeight: 600 }}>edited</span>}
            </div>
            <input
              type="text"
              placeholder={field.placeholder}
              value={val}
              onChange={e => onChange(field.key, e.target.value)}
              style={{
                ...fieldInput,
                border: `1.5px solid ${missing ? "#f0c040" : changed ? ACCENT_40 : "#E4E4E0"}`,
              }}
              onFocus={e => { e.target.style.borderColor = missing ? "#d4a800" : ACCENT; }}
              onBlur={e  => { e.target.style.borderColor = missing ? "#f0c040" : changed ? ACCENT_40 : "#E4E4E0"; }}
            />
          </div>
        );
      })}
    </div>

    <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
      {hasChanges && !saved && (
        <button
          style={{
            background: ACCENT, color: "#fff", border: "none",
            borderRadius: "10px", padding: "8px 16px",
            fontSize: "12px", fontWeight: 700,
            cursor: isSaving ? "not-allowed" : "pointer",
            opacity: isSaving ? 0.6 : 1, fontFamily: "inherit",
          }}
          disabled={isSaving}
          onClick={onSave}
        >
          {isSaving ? "Saving…" : "Save to profile"}
        </button>
      )}
      {saved     && <span style={{ fontSize: "12px", color: "#27ae60", fontWeight: 600 }}>✓ Saved to profile</span>}
      {saveError && <span style={{ fontSize: "12px", color: "#c0392b" }}>⚠ {saveError}</span>}
      {hasMissingUnfilled && (
        <span style={{ fontSize: "12px", color: "#9a7800" }}>
          Fill in required fields above · or{" "}
          <button
            style={{
              background: "none", border: "none", color: "#9a7800",
              textDecoration: "underline", cursor: "pointer",
              fontSize: "12px", padding: 0,
            }}
            onClick={onChooseOtherTemplate}
          >
            choose another template
          </button>
        </span>
      )}
    </div>
  </div>
);

export default StepContent;
